import DashboardWrapper from '../../layout/DashboardWrapper';
import StatisticsCard from '../../components/cards/StatisticsCard';
import { BookOpenCheck, Award, Medal, MoveUpRight, Link as LinkIcon } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useParams, useSearch } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useToken } from '../../hooks/useToken';
import { api } from '../../lib/api';
import { usePagination } from '../../hooks/usePagination';
import { PaginationControls } from '../../components/sections/PaginationControls';
import { DataNotFoundSection } from '../../components/sections/DataNotFoundSection';
import CourseCard from '../../components/cards/CourseCard.jsx';
import { CourseSkeleton } from '../../components/skeletons/CourseSkeleton.jsx';
import StatisticsCardSkeleton from '../../components/skeletons/StatisticsCardSkeleton';
import GradebookTemplate from '../../components/cards/GradebookTemplate';
import QuickAccessLinksSection from '../../components/sections/QuickAccessLinksSection';
// import { PathwayPageSkeleton } from "../../components/skeletons/PathwayPageSkeleton.jsx";

const PathwayPage = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [pathway, setPathway] = useState(null);
    const [pathwayCourses, setPathwayCourses] = useState([]);
    const [gradebook, setGradebook] = useState(null);
    const [averageScore, setAverageScore] = useState(0);
    const [totalXp, setTotalXp] = useState(0);
    const { pathwayId } = useParams({ from: '/pathway/$pathwayId' });

    const { user } = useUser();
    const token = useToken();
    const { status, difficulty, sort_by, sort_order } = useSearch({ from: '/pathway/$pathwayId' });

    const getGradeFromPercentage = percentage => {
        if (percentage === null || percentage === undefined) return '';
        if (percentage >= 80) return 'DISTINCTION';
        if (percentage >= 60) return 'MERIT';
        if (percentage >= 40) return 'PASS';
        return 'In Progress';
    };

    const printRef = useRef(null);

    const handleSmallPrint = () => {
        if (!printRef.current) return;
        try {
            const printContents = printRef.current.innerHTML;
            const newWin = window.open('', '_blank');
            if (!newWin) return window.print();
            newWin.document.open();
            // Clone current head to preserve styles
            const headHtml = document.head.innerHTML;
            newWin.document.write(`<!doctype html><html><head>${headHtml}</head><body>${printContents}</body></html>`);
            newWin.document.close();
            newWin.focus();
            // Allow browser to render before invoking print
            setTimeout(() => {
                newWin.print();
                // Optionally close the window after printing
                // newWin.close();
            }, 250);
        } catch (err) {
            console.error('Print failed, falling back to window.print()', err);
            window.print();
        }
    };

    const fetchPathwayData = useCallback(async () => {
        if (!user || !pathwayId || !token) return;
        setIsLoading(true);
        try {
            // 1. Fetch all pathway enrollments for the user
            const response = await api.getUserPathwayEnrollments(token);
            const allPathways = response?.pathways || [];

            // 2. Find the specific pathway by ID
            const currentPathway = allPathways.find(p => p.id.toString() === pathwayId);
            if (!currentPathway) {
                console.error('Pathway not found for the current user.');
                setPathway(null);
                return;
            }

            // 3. Fetch progress and associated courses for that pathway
            // Build optional query params for pathway courses
            const params = {};
            if (status) params.status = Number(status);
            if (difficulty) params.difficulty = Number(difficulty);
            if (sort_by) params.sort_by = sort_by;
            if (sort_order) params.sort_order = sort_order;
            console.debug('fetchPathwayData - pathway courses params:', params);

            const [progressResponse, coursesResponse] = await Promise.all([
                api.getPathwayProgress(user.id, pathwayId, token),
                api.getPathwayCourses(pathwayId, token, params),
            ]);

            const pathwayWithDetails = {
                ...currentPathway,
                progress: progressResponse?.progress?.progress ?? progressResponse?.progress ?? 0,
                associatedCourses: coursesResponse?.courses || [],
            };

            setPathway(pathwayWithDetails);
        } catch (err) {
            console.error('Error fetching pathway data:', err);
            setPathway(null);
        }
    }, [pathwayId, user, token, status, difficulty, sort_by, sort_order]);

    const fetchCourseDetails = useCallback(
        async (courseId, courseSlug) => {
            if (!token || !user?.id) return null;
            try {
                // Fetch course content (lessons, projects, quizes)
                const contentResponse = await api.getCourseContent(courseSlug, token);
                const topics = contentResponse?.topics || [];

                // Extract lessons, projects, and quizes from topics
                let lessons = [];
                let projects = [];
                let quizes = [];
                topics.forEach(topic => {
                    if (Array.isArray(topic.lessons)) lessons.push(...topic.lessons);
                    if (Array.isArray(topic.projects)) projects.push(...topic.projects);
                    if (Array.isArray(topic.quizes)) quizes.push(...topic.quizes);
                });

                // Fetch completions in parallel
                const [lessonsResponse, projectsResponse, quizesResponse] = await Promise.all([
                    api.getLessonCompletions(user.id, courseId, token).catch(() => ({ completed_lessons: [] })),
                    api.getProjectCompletions(user.id, courseId, token).catch(() => ({ completions: [] })),
                    api.getQuizCompletions(user.id, courseId, token).catch(() => ({ completions: [] })),
                ]);

                const completions = lessonsResponse?.completed_lessons || lessonsResponse?.data?.completed_lessons || [];
                const projectCompletions = (projectsResponse?.completions || projectsResponse?.data?.completions || []).map(
                    c => c.project_id
                );
                const quizCompletions = (quizesResponse?.completions || quizesResponse?.data?.completions || []).map(
                    q => q.quiz_id
                );

                return {
                    lessons,
                    projects,
                    quizes,
                    completions,
                    projectCompletions,
                    quizCompletions,
                };
            } catch (err) {
                console.error('Error fetching course details:', err);
                return null;
            }
        },
        [token, user?.id]
    );

    const fetchCourses = useCallback(async () => {
        try {
            if (pathway?.associatedCourses) {
                const coursesWithDetailsPromises = pathway.associatedCourses.map(async course => {
                    try {
                        const details = await fetchCourseDetails(course.id, course.slug);
                        if (details) {
                            return {
                                ...course,
                                lessons: details.lessons,
                                projects: details.projects,
                                quizes: details.quizes,
                                completions: details.completions,
                                projectCompletions: details.projectCompletions,
                                quizCompletions: details.quizCompletions,
                            };
                        }
                        return course;
                    } catch (error) {
                        console.error(`Error fetching details for course ${course.id}:`, error);
                        return course; // Return the original course if details fail
                    }
                });

                const coursesWithDetails = await Promise.all(coursesWithDetailsPromises);
                setPathwayCourses(coursesWithDetails);
            }
        } catch (err) {
            console.error('Error fetching user courses:', err);
        }
    }, [pathway?.associatedCourses, fetchCourseDetails]);

    const fetchPathwayGradebook = useCallback(async () => {
        if (!user?.id || !pathway?.id || !token) return;
        try {
            const response = await api.getPathwayGradebook(user.id, pathway.id, token);
            console.log('Pathway Gradebook:', response);
            setGradebook(response?.gradebook || response?.data?.gradebook || response);
        } catch (err) {
            console.error('Error fetching pathway gradebook:', err);
        }
    }, [token, user?.id, pathway?.id]);

    const completedCourses = pathwayCourses.filter(course => course.progress * 100 === 100);
    const coursesPagination = usePagination(pathwayCourses, 3);

    useEffect(() => {
        if (user && pathwayId && token) {
            fetchPathwayData();
        } else if (!token) {
            setIsLoading(false);
        }
    }, [user, pathwayId, fetchPathwayData, token]);

    useEffect(() => {
        if (pathway) {
            fetchCourses().finally(() => setIsLoading(false));
            fetchPathwayGradebook();
        }
    }, [pathway, fetchCourses, fetchPathwayGradebook]);

    useEffect(() => {
        if (gradebook && gradebook.gradebook && gradebook.gradebook.length > 0) {
            let totalPercentage = 0;
            let totalUserScore = 0;
            let coursesWithScores = 0;
            gradebook.gradebook.forEach(course => {
                const userScore = course.user_score || 0;
                const totalScore = course.total_score || 0;
                if (totalScore > 0) {
                    totalPercentage += (userScore / totalScore) * 100;
                    coursesWithScores++;
                }
                totalUserScore += userScore;
            });
            setAverageScore(coursesWithScores > 0 ? totalPercentage / coursesWithScores : 0);
            setTotalXp(totalUserScore);
        }
    }, [gradebook]);

    useEffect(() => {
        if (pathway) {
            document.title = `${pathway.title} Pathway | Zindua LMS`;
        }
    }, [pathway]);

    if (isLoading) {
        return (
            <DashboardWrapper>
                <div className="w-full">
                    <div className="h-8 w-48 bg-neutral-200 rounded animate-pulse my-3" />
                    <div className="grid grid-cols-1 md:grid-cols-3  gap-4 my-5">
                        <StatisticsCardSkeleton />
                        <StatisticsCardSkeleton />
                        <StatisticsCardSkeleton />
                    </div>
                    <div className="w-full flex flex-wrap lg:flex-nowrap flex-col gap-2 md:flex-row justify-between items-start">
                        <div className="w-full md:max-w-3xl flex grow flex-col">
                            <div className="h-10 w-full bg-neutral-200 rounded animate-pulse mb-4" />
                            <div className="flex flex-col gap-3">
                                <CourseSkeleton />
                                <CourseSkeleton />
                                <CourseSkeleton />
                            </div>
                        </div>
                        <div className="border border-neutral-300 lg:max-w-87.5 w-full rounded-lg p-4 flex flex-col gap-4 max-md:mt-4">
                            <div className="h-6 w-32 bg-neutral-200 rounded animate-pulse" />
                            <div className="flex flex-col gap-3">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="flex justify-between items-center">
                                        <div className="h-4 w-32 bg-neutral-200 rounded animate-pulse" />
                                        <div className="h-4 w-12 bg-neutral-200 rounded animate-pulse" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardWrapper>
        );
    }

    if (!pathway) {
        return (
            <DashboardWrapper>
                <DataNotFoundSection title="Pathway not found." />
            </DashboardWrapper>
        );
    }

    return (
        <DashboardWrapper>
            <div className="w-full">
                <h1 className="my-3 capitalize">{pathway?.title} Pathway</h1>
                <p>{pathway?.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-5">
                    <StatisticsCard
                        title="Completed Courses"
                        icon={<BookOpenCheck size={20} />}
                        value={`${completedCourses.length}/${pathway?.associatedCourses?.length || 0}`}
                        subtitle={`${(pathway?.associatedCourses?.length || 0) - completedCourses.length} course(s) remains`}
                    />
                    <StatisticsCard
                        title="Average Score"
                        icon={<Award size={20} />}
                        value={`${averageScore.toFixed(2)}%`}
                        subtitle="Across completed courses"
                    />
                    <StatisticsCard
                        title="Total XP Earned"
                        icon={<Medal size={20} />}
                        value={totalXp.toLocaleString()}
                        subtitle="Across courses in pathway"
                    />
                </div>
                <div className="w-full grid lg:grid-cols-3">
                    <div defaultValue="course" className="w-full md:max-w-3xl flex grow flex-col col-span-1 lg:col-span-2">
                        <div className="w-full">
                            <div className="flex flex-col max-w-3xl gap-3 mt-4">
                                {isLoading ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <CourseSkeleton />
                                        <CourseSkeleton />
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex flex-col gap-4">
                                            {coursesPagination.currentData.map(course => (
                                                <CourseCard key={course.id} course={course} dashboard={true} />
                                            ))}
                                            {coursesPagination.totalItems > 3 && (
                                                <PaginationControls
                                                    pagination={coursesPagination}
                                                    totalItems={pathwayCourses.length}
                                                    itemsPerPage={3}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                                {pathwayCourses.length === 0 && !isLoading && <DataNotFoundSection title="No courses found" />}
                            </div>
                        </div>
                    </div>
                    <div className="col-span-1 w-full sticky top-0 p-4 flex flex-col gap-4">
                        <div className="border border-neutral-300  rounded-lg flex flex-col grow gap-2 max-md:mt-4">
                            <div className="py-2 px-2 border-b border-neutral-300">
                                <h4 className="mb-1">Pathway Gradebook</h4>
                                <p className="text-sm">View your performance across the courses</p>
                            </div>
                            <div className="px-4 py-2">
                                {gradebook && gradebook.gradebook && gradebook.gradebook.length > 0 ? (
                                    <>
                                        {gradebook.gradebook.map((course, index) => {
                                            const userScore = course.user_score || 0;
                                            const totalScore = course.total_score || 0;
                                            const rawPercentage = totalScore > 0 ? (userScore / totalScore) * 100 : 0;
                                            const percentage = Math.min(100, Math.max(0, rawPercentage));
                                            const isCompleted = percentage === 100;
                                            {
                                                console.log(percentage);
                                            }

                                            return (
                                                <div
                                                    key={course.course_id}
                                                    className={`grid grid-cols-3 w-full items-center gap-4 py-3 text-neutral-700 ${
                                                        index === gradebook.gradebook.length - 1
                                                            ? ''
                                                            : 'border-b border-neutral-300'
                                                    }`}
                                                >
                                                    <p className="truncate overflow-hidden">{course.course_title}</p>
                                                    {/* Middle column: reserved (hide percentage as requested) */}
                                                    <div />
                                                    {/* Right column: show grade for completed, or outlined 'In Progress' badge for in-progress */}
                                                    {isCompleted ? (
                                                        <div className="justify-center text-sm">
                                                            {getGradeFromPercentage(percentage)}
                                                        </div>
                                                    ) : (
                                                        <Badge
                                                            className="justify-center border border-accent text-accent max-lg:text-xs line-clamp-1"
                                                            variant={'outline'}
                                                        >
                                                            incomplete
                                                        </Badge>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {/* Download Transcript (all screen sizes): open printable markup in a new window to preserve layout */}
                                        <div>
                                            <Button variant="link" className="w-full mt-4" onClick={handleSmallPrint}>
                                                Download Transcript
                                                <MoveUpRight size={20} />
                                            </Button>

                                            {/* Positioned off-screen but still rendered so innerHTML works properly */}
                                            <div
                                                className="absolute -left-2499.75 top-0 print:static print:left-0"
                                                ref={printRef}
                                            >
                                                <GradebookTemplate
                                                    gradebook={gradebook.gradebook}
                                                    pathwayTitle={pathway?.title}
                                                    userName={user?.fullName || 'Student'}
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <DataNotFoundSection title="No gradebook data available." />
                                )}
                            </div>
                        </div>
                        <div>
                            <QuickAccessLinksSection
                                links={[
                                    {
                                        icon: <LinkIcon size={20} />,
                                        title: 'Enrolment Confirmation',
                                        description: 'Here is your official proof of enrolment',
                                        href: '#',
                                    },
                                    {
                                        icon: <LinkIcon size={20} />,
                                        title: 'Enrolment Contract',
                                        description: 'Help us improve your experience',
                                        href: '#',
                                    },
                                    {
                                        icon: <LinkIcon size={20} />,
                                        title: 'Deferral/Withdrawal Request',
                                        description: 'Routes to your Program Manager',
                                        href: '#',
                                    },
                                    {
                                        icon: <LinkIcon size={20} />,
                                        title: 'Request Refund',
                                        description: 'For overpayment or after program withdrawal',
                                        href: '#',
                                    },
                                ]}
                                cta={{ to: '/student-id', text: 'View my student ID' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </DashboardWrapper>
    );
};

export default PathwayPage;
